import React, { useState } from "react";
import { Undo2 } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

// Lab-side statuses a Dispatch line can be sent back into. "Assigned" is the
// default — the item returns to its already-assigned technician's queue
// exactly where it left off, without forcing a re-triage decision.
export const SEND_BACK_TO_LAB_STATUSES = [
  "assigned", "received", "in_progress",
  "parts_required", "awaiting_spares",
  "awaiting_customer_approval", "awaiting_quote",
];

/**
 * Sends a Dispatch-stage line back into the Active Lab queue. Deliberately
 * does NOT touch discipline/technician_id/technician_name — the line keeps
 * its original discipline and assigned technician exactly as-is, it's only
 * the workflow status that changes. Closed lines go through the dedicated
 * /reopen endpoint (also clears closed_at/closed_by); anything else uses the
 * plain status-patch endpoint.
 */
export function SendBackToLabModal({ item, onClose, onDone }) {
  const [status, setStatus] = useState("assigned");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!window.confirm(`Send this line back to the Active Lab as "${status.replace(/_/g, " ")}"?`)) return;
    setBusy(true);
    try {
      if (item.status === "closed") {
        await api.post(`/job-items/${item.id}/reopen`, { target_status: status, note: note || undefined });
      } else {
        await api.patch(`/job-items/${item.id}/status`, { status, notes: note || undefined });
      }
      toast.success(`Sent back to the lab as "${status.replace(/_/g, " ")}"`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Undo2}
      title="Send back to Lab"
      subtitle={`${item.description || ""} · DN ${item.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="send-back-to-lab-cancel">Cancel</button>
        <button className="btn gold" onClick={confirm} disabled={busy} data-testid="send-back-to-lab-confirm">
          <Undo2 size={13} /> {busy ? "Sending…" : "Send back to Lab"}
        </button>
      </>}>

      <p className="t-mut" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Keeps the original discipline ({item.discipline || "—"}) and assigned technician
        ({item.technician_name || "Unassigned"}) — only the status changes.
      </p>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Active Lab status
      </label>
      <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}
        data-testid="send-back-to-lab-status" style={{ marginBottom: 10 }}>
        {SEND_BACK_TO_LAB_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Optional note
      </label>
      <input className="input" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Why is this going back to the lab?" data-testid="send-back-to-lab-note" />
    </Modal>
  );
}
